import { useCallback, useEffect, useState } from "react";
import { adminGqlRequest } from "@/lib/graphql";

const ADMIN_SELLERS_QUERY = `
  query AdminSellers($input: AdminSellersInput) {
    adminSellers(input: $input) {
      items {
        id
        name
        ownerName
        ownerEmail
        status
        plan
        totalProducts
        activeOrders
        revenue30d
        lowStockCount
        syncStatus
        marketplaces
        createdAt
      }
      totalCount
      page
      pageSize
      totalPages
    }
  }
`;

export interface SellerListItem {
  id: string;
  name: string;
  ownerName: string | null;
  ownerEmail: string | null;
  status: string;
  plan: string;
  totalProducts: number;
  activeOrders: number;
  revenue30d: number;
  lowStockCount: number;
  syncStatus: string;
  marketplaces: string[];
  createdAt: string;
}

export interface SellersPayload {
  items: SellerListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface SellersFilters {
  search?: string;
  status?: string;
  page?: number;
  pageSize?: number;
}

export function useSellers(filters: SellersFilters = {}) {
  const [data, setData] = useState<SellersPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminGqlRequest<{ adminSellers: SellersPayload }>(
        ADMIN_SELLERS_QUERY,
        {
          input: {
            search: filters.search || undefined,
            status: filters.status || undefined,
            page: filters.page ?? 1,
            pageSize: filters.pageSize ?? 10,
          },
        },
      );
      setData(result.adminSellers);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load sellers");
    } finally {
      setLoading(false);
    }
  }, [filters.search, filters.status, filters.page, filters.pageSize]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, loading, error, refetch: fetch };
}

const SET_SELLER_STATUS_MUTATION = `
  mutation SetSellerStatus($input: SetSellerStatusInput!) {
    setSellerStatus(input: $input) {
      id
      status
    }
  }
`;

export async function setSellerStatus(
  workspaceId: string,
  status: "ACTIVE" | "SUSPENDED",
): Promise<void> {
  await adminGqlRequest(SET_SELLER_STATUS_MUTATION, {
    input: { workspaceId, status },
  });
}
